import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    IconButton,
    Typography,
    Paper,
    Grid,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { getAPI, postAPI, putAPI, deleteAPI } from "../../../network";

const QuestionnairePage = () => {
    const [title, setTitle] = useState("");
    const [questions, setQuestions] = useState([{ text: "", order: 1 }]);
    const [active, setActive] = useState(true);
    const [questionnaires, setQuestionnaires] = useState([]);
    const [editId, setEditId] = useState(null);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [questionnaireToDelete, setQuestionnaireToDelete] = useState(null);
    const [isDeletingActive, setIsDeletingActive] = useState(false);

    let companyId = localStorage.getItem("companyIdLS");
    if (companyId === null) {
        companyId = undefined;
    }

    const fetchQuestionnaires = async () => {
        const data = await getAPI(`/building/questionnaire?companyId=${companyId}`);
        if (data) {
            setQuestionnaires(data);
        }
    };

    useEffect(() => {
        fetchQuestionnaires();
    }, []);

    const handleAddQuestion = () => {
        setQuestions([...questions, { text: "", order: questions.length + 1 }]);
    };

    const handleRemoveQuestion = (index) => {
        const updated = [...questions];
        updated.splice(index, 1);
        setQuestions(updated.map((q, i) => ({ ...q, order: i + 1 })));
    };

    const handleChangeQuestion = (index, value) => {
        const updated = [...questions];
        updated[index].text = value;
        setQuestions(updated);
    };

    const resetForm = () => {
        setTitle("");
        setQuestions([{ text: "", order: 1 }]);
        setActive(true);
        setEditId(null);
    };

    const handleSubmit = async () => {
        const payload = { title, companyId, questions, active };

        if (editId) {
            const updated = await putAPI(`/building/questionnaire/${editId}`, payload);
            if (updated) {
                resetForm();
                fetchQuestionnaires();
            }
        } else {
            const created = await postAPI("/building/questionnaire", payload);
            if (created) {
                resetForm();
                fetchQuestionnaires();
            }
        }
    };

    const handleEdit = (questionnaire) => {
        setEditId(questionnaire._id);
        setTitle(questionnaire.title);
        setQuestions([...questionnaire.questions]);
        setActive(questionnaire.active);
    };

    const handleDeletePrompt = (q) => {
        setQuestionnaireToDelete(q);
        setIsDeletingActive(q.active);
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (questionnaireToDelete) {
            const deleted = await deleteAPI(`/building/questionnaire/${questionnaireToDelete._id}`);
            if (deleted) {
                fetchQuestionnaires();
            }
        }
        setShowDeleteDialog(false);
        setQuestionnaireToDelete(null);
    };

    return (
        <Box p={3}>
            <Typography variant="h5" mb={2}>
                {editId ? "Edit Questionnaire" : "Create Questionnaire"}
            </Typography>

            <Paper sx={{ p: 3, mb: 4 }}>
                <TextField
                    label="Title"
                    fullWidth
                    margin="normal"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />

                {questions.map((q, idx) => (
                    <Box key={idx} display="flex" alignItems="center" mb={2}>
                        <TextField
                            label={`Q${idx + 1}`}
                            fullWidth
                            value={q.text}
                            onChange={(e) => handleChangeQuestion(idx, e.target.value)}
                        />
                        {questions.length > 1 && (
                            <IconButton onClick={() => handleRemoveQuestion(idx)}>
                                <Delete />
                            </IconButton>
                        )}
                    </Box>
                ))}

                <Button startIcon={<Add />} onClick={handleAddQuestion}>
                    Add Question
                </Button>

                <Box mt={2}>
                    <Typography variant="subtitle1" mb={1}>Status</Typography>
                    <Box>
                        <label>
                            <input
                                type="radio"
                                name="active"
                                value="true"
                                checked={active === true}
                                onChange={() => setActive(true)}
                            />
                            Active
                        </label>
                        <label style={{ marginLeft: '20px' }}>
                            <input
                                type="radio"
                                name="active"
                                value="false"
                                checked={active === false}
                                onChange={() => setActive(false)}
                            />
                            Inactive
                        </label>
                    </Box>
                </Box>

                <Box mt={3}>
                    <Button variant="contained" onClick={handleSubmit}>
                        {editId ? "Update" : "Create"}
                    </Button>
                    {editId && (
                        <Button onClick={resetForm} sx={{ ml: 2 }}>
                            Cancel
                        </Button>
                    )}
                </Box>
            </Paper>

            <Typography variant="h6" mb={1}>Existing Questionnaires</Typography>
            {questionnaires.map((q) => (
                <Paper key={q._id} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={10}>
                            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                                {q.title}
                            </Typography>

                            <Typography variant="subtitle1" sx={{ fontStyle: "italic", mb: 2 }}>
                                Status: {q.active ? "Active" : "Inactive"}
                            </Typography>

                            {q.questions.map((ques) => (
                                <Typography key={ques.order} variant="subtitle1" sx={{ mb: 1 }}>
                                    Q{ques.order}: {ques.text}
                                </Typography>
                            ))}
                        </Grid>

                        <Grid item xs={2} display="flex" justifyContent="flex-end">
                            <IconButton onClick={() => handleEdit(q)}>
                                <Edit />
                            </IconButton>
                            <IconButton onClick={() => handleDeletePrompt(q)}>
                                <Delete />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Paper>
            ))}

            {/* Modal */}
            <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
                <DialogTitle>
                    {isDeletingActive ? "Cannot Delete Active Questionnaire" : "Confirm Deletion"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mt: 2 }}>
                        {isDeletingActive
                            ? "Please set another questionnaire as active before deleting this one."
                            : "Are you sure you want to delete this questionnaire?"}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                    {!isDeletingActive && (
                        <Button onClick={confirmDelete} color="error">
                            Delete
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default QuestionnairePage;
